// src/pages/ToolPage.tsx
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Container, Box, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { menuConfig } from '../config/menuConfig';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PdfMerger from '../features/pdf/pdf-merger/PdfMerger';
import PdfSplitter from '../features/pdf/pdf-splitter/PdfSplitter';
import PdfCompressor from '../features/pdf/pdf-compressor/PdfCompressor';
import PdfEncryptor from '../features/pdf/pdf-encryptor/PdfEncryptor';
import PdfDecryptor from '../features/pdf/pdf-decryptor/PdfDecryptor';
import PdfReorder from '../features/pdf/pdf-reorder/PdfReorder';
import { SvgIconComponent } from '@mui/icons-material';
import JsonParser from '../features/developer/json-parser/JsonParser';
import JwtTool from '../features/developer/jwt-tool/JwtTool';
import FileToBase64Converter from '../features/developer/file-to-base64/FileToBase64Converter';
import Base64ToFileConverter from '../features/developer/base64-to-file/Base64ToFileConverter';
import TimestampConverter from '../features/developer/timestamp-converter/TimestampConverter';

import CaseConverter from '../features/text/case-converter/CaseConverter';
import NamingCaseConverter from '../features/text/naming-case-converter/NamingCaseConverter';
import WhitespaceTrimmer from '../features/text/whitespace-trimmer/WhitespaceTrimmer';
import FindAndReplace from '../features/text/find-replace/FindAndReplace';
import WordCounter from '../features/text/word-counter/WordCounter';

import * as Converters from '../features/unit/converters';
import AiChat from '../features/ai/AiChat';


const toolComponentMap: { [key: string]: React.ComponentType } = {
    'pdf-merge': PdfMerger,
    'pdf-split': PdfSplitter,
    'pdf-compress': PdfCompressor,
    'pdf-encrypt': PdfEncryptor,
    'pdf-decrypt': PdfDecryptor,
    'pdf-reorder': PdfReorder,
    'json-parser': JsonParser,
    'jwt-tool': JwtTool,
    'file-to-base64': FileToBase64Converter,
    'base64-to-file': Base64ToFileConverter,
    'timestamp-converter': TimestampConverter,

    'case-converter': CaseConverter,
    //'naming-case-converter': NamingCaseConverter,
    'whitespace-trimmer': WhitespaceTrimmer,   // Ready for implementation
    'find-replace': FindAndReplace,           // Ready for implementation
    'word-counter': WordCounter,             // Ready for implementation

    // Add all converters using their menuConfig 'id' as the key
    'length-converter': Converters.LengthConverter,
    'mass-converter': Converters.MassConverter,
    'area-converter': Converters.AreaConverter,
    'volume-converter': Converters.VolumeConverter,
    'time-converter': Converters.TimeConverter,
    'speed-converter': Converters.SpeedConverter,
    'acceleration-converter': Converters.AccelerationConverter,
    'force-converter': Converters.ForceConverter,
    'pressure-converter': Converters.PressureConverter,
    'energy-converter': Converters.EnergyConverter,
    'power-converter': Converters.PowerConverter,
    'temperature-converter': Converters.TemperatureConverter,
    'angle-converter': Converters.AngleConverter,
    'frequency-converter': Converters.FrequencyConverter,
    'electric-charge-converter': Converters.ElectricChargeConverter,
    'electric-current-converter': Converters.ElectricCurrentConverter,
    'voltage-converter': Converters.VoltageConverter,
    'resistance-converter': Converters.ResistanceConverter,
    'conductance-converter': Converters.ConductanceConverter,
    'magnetic-flux-converter': Converters.MagneticFluxConverter,
    'magnetic-field-strength-converter': Converters.MagneticFieldStrengthConverter,
    'density-converter': Converters.DensityConverter,
    'concentration-converter': Converters.ConcentrationConverter,
    'radiation-dose-converter': Converters.RadiationDoseConverter,
    
    // AI tools
    'ai-chat': AiChat,
};

const ToolPage: React.FC = () => {
    const { t } = useTranslation();
    const { category, tool: toolId } = useParams<{ category: string; tool: string }>();
    const navigate = useNavigate();

    const currentToolConfig = menuConfig
        .find(cat => cat.id === category)
        ?.tools.find(t => t.id === toolId);

    const ToolComponent = toolId ? toolComponentMap[toolId] : null;
    // Assuming icon is part of your tool config
    const IconComponent = currentToolConfig?.icon as SvgIconComponent | undefined;

    if (!currentToolConfig || !ToolComponent) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Header Section for Not Found */}
                <Box sx={{ position: 'relative', textAlign: 'center' }}>
                    <IconButton
                        onClick={() => navigate(`/tools/${category}`)}
                        aria-label="Back to Category"
                        sx={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography
                        variant="h3"
                        component="h1"
                        gutterBottom
                        sx={{ color: 'error.main' }} // Red color for title when not found
                    >
                        {t('tool_not_found', { ns: 'tools' })}
                    </Typography>
                </Box>

                <Typography variant="body1" component="p" sx={{ textAlign: 'center', color: 'text.secondary', mb: 4 }}>
                    {t('tool_not_found_desc', { ns: 'tools' })}
                </Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header Section */}
            <Box sx={{ position: 'relative', textAlign: 'center' }}>
                <IconButton
                    onClick={() => navigate(`/tools/${category}`)}
                    aria-label="Back to Category"
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h3" component="h1" gutterBottom>
                    {/* Box to group icon and text for proper centering */}
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        {IconComponent && <IconComponent sx={{ fontSize: 'inherit' }} />}
                        {t(currentToolConfig.nameKey, { ns: 'tools' })}
                    </Box>
                </Typography>
            </Box>

            <Typography variant="body1" component="p" sx={{ textAlign: 'center', color: 'text.secondary', mb: 4 }}>
                {t(currentToolConfig.descKey, { ns: 'tools' })}
            </Typography>

            {/* Tool Component */}
            <Box sx={{ mt: 2 }}>
                <ToolComponent />
            </Box>
        </Container>
    );
};

export default ToolPage;